import { ClientAdministrationRow, ContractType } from "@/data/models/ClientAdministrationRow";
import { matchesGroupedSelection, orderedSelection } from "@/utils/GroupedFilterSelection";

/**
 * The filters of the admin portal's clients list: the same eleven, with the same meaning, as the
 * IRES Admin Android app's clients list (`ClientsOverviewViewModel`), so both apps show the same
 * clients while they run side by side, then MASTER (Taimoor, 2026-10-12) and LIVE / NOT_LIVE
 * ("Kein Echtbetrieb", ticket 2610043), which the Android list does not have.
 */
export enum ClientListFilter {
  ALL = "ALL",
  IRES = "IRES",
  CLIENT = "CLIENT",
  SERVICE_CONTRACT = "SERVICE_CONTRACT",
  CONTRACT_PURCHASE = "CONTRACT_PURCHASE",
  CONTRACT_PERMANENT_RENT = "CONTRACT_PERMANENT_RENT",
  CONTRACT_TESTING = "CONTRACT_TESTING",
  HAS_APP = "HAS_APP",
  DELETED = "DELETED",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  /** A group's master (`isMasterClient`); absent from IRES Cloud builds before 2026-10-12, so none match. */
  MASTER = "MASTER",
  /** Not marked "Kein Echtbetrieb": every client an older IRES Cloud serves. */
  LIVE = "LIVE",
  /** Marked "Kein Echtbetrieb": a test, demo or warehouse account (ticket 2610043). */
  NOT_LIVE = "NOT_LIVE",
  /** Gesperrt: nobody can sign in (#2610067); absent from IRES Cloud builds before it, so none match. */
  BLOCKED = "BLOCKED",
}

/** In the order of the Android app's filter menu, then MASTER, LIVE, NOT_LIVE and BLOCKED. */
export const CLIENT_LIST_FILTERS: ClientListFilter[] = Object.values(ClientListFilter);

/** Parses a filter from a route query value, falling back to ALL for anything unknown. */
export function parseClientListFilter(value: unknown): ClientListFilter {
  return CLIENT_LIST_FILTERS.includes(value as ClientListFilter)
    ? (value as ClientListFilter)
    : ClientListFilter.ALL;
}

/**
 * Whether a client passes a filter.
 *
 * Deleted clients appear under DELETED **only**: every other filter, ALL included, excludes them.
 * HAS_APP is the Android list's definition (`hasApp` or a target app build), which deliberately is
 * not the one the Android detail screen uses; that inconsistency is left for the programme's field
 * audit rather than fixed on one side only. An absent `isActive` counts as active.
 */
export function matchesClientListFilter(
  client: ClientAdministrationRow,
  filter: ClientListFilter
): boolean {
  if (filter === ClientListFilter.DELETED) {
    return client.deleted;
  }

  if (client.deleted) {
    return false;
  }

  switch (filter) {
    case ClientListFilter.IRES:
      return client.clientType === "IRES";
    case ClientListFilter.CLIENT:
      return client.clientType === "CLIENT";
    case ClientListFilter.SERVICE_CONTRACT:
      return client.clientType === "CLIENT" && client.hasServiceContractForFirmwaresAndConfigs;
    case ClientListFilter.CONTRACT_PURCHASE:
      return client.contractTypes.includes(ContractType.PURCHASE);
    case ClientListFilter.CONTRACT_PERMANENT_RENT:
      return client.contractTypes.includes(ContractType.PERMANENT_RENT);
    case ClientListFilter.CONTRACT_TESTING:
      return client.contractTypes.includes(ContractType.TESTING);
    case ClientListFilter.HAS_APP:
      return client.hasApp || client.targetAppApk !== null;
    case ClientListFilter.ACTIVE:
      return client.isActive !== false;
    case ClientListFilter.INACTIVE:
      return client.isActive === false;
    case ClientListFilter.MASTER:
      return client.isMasterClient === true;
    case ClientListFilter.LIVE:
      return !client.notLiveReason;
    case ClientListFilter.NOT_LIVE:
      return !!client.notLiveReason;
    case ClientListFilter.BLOCKED:
      return client.blocked === true;
    default:
      return true;
  }
}

/**
 * Whether a client matches the search: a case-insensitive substring of its name, its client number
 * or its Mandantenkürzel. The query is trimmed, so a stray space does not empty the list; an empty query matches
 * everything.
 */
export function matchesClientListSearch(client: ClientAdministrationRow, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();

  if (needle === "") {
    return true;
  }

  return [client.clientName, client.clientNumber, client.iresProjectNumberPrefix].some(
    (value) => typeof value === "string" && value.toLocaleLowerCase().includes(needle)
  );
}

/**
 * The sort key the Android list uses: the name (or the email when there is no name), then the
 * client number.
 */
function sortKey(client: ClientAdministrationRow): string {
  const name = client.clientName?.trim() || client.email?.trim() || "";
  const number = client.clientNumber ? ` (${client.clientNumber})` : "";

  return (name + number).trim();
}

/** Search, then filter, then sort by name; the input is not modified. */
export function selectClientListRows(
  clients: ClientAdministrationRow[],
  filter: ClientListFilter,
  query: string
): ClientAdministrationRow[] {
  return clients
    .filter((client) => matchesClientListSearch(client, query) && matchesClientListFilter(client, filter))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b), "de", { sensitivity: "base" }));
}


/**
 * The session-storage key that marks a client as the one last opened from the list. Prefixed, as the
 * vehicle list's is, because the last-visited slot is shared by the top-level lists and a client id
 * must never be mistaken for another list's item.
 */
export function clientListLastVisitedKey(clientId: string): string {
  return `ADMIN_CLIENTS/${clientId}`;
}

/**
 * Session storage key (per client, see `SecureLocalStorage`) for the list address a client was opened
 * from, so the detail can return to the very same kept-alive screen.
 */
export const CLIENT_LIST_RETURN_PATH_KEY = "ADMIN_CLIENTS_RETURN_PATH";

/**
 * The address the detail's Zurück should go to: the stored one if it is the clients list (with
 * whatever query it had), else the plain list. Anything else stored is ignored, so a stale or
 * tampered value can never send the user elsewhere.
 *
 * @param stored - the stored address, if any.
 * @param listPath - the clients list's route path, e.g. "/main/admin/clients".
 * @returns the address to push.
 */
export function clientListReturnPath(stored: string | null | undefined, listPath: string): string {
  if (typeof stored !== "string") {
    return listPath;
  }

  const [path] = stored.split("?");
  return path === listPath ? stored : listPath;
}

/**
 * The Mandanten list's multi-filter (Hans, 2026-10-01: "Perhaps Multi-Filter would be meaningful?").
 * The single filters regrouped, on the shared rule in `GroupedFilterSelection.ts` (any ticked filter
 * within a group, all groups with a tick across).
 */
export enum ClientListFilterGroup {
  TYPE = "TYPE",
  CONTRACT = "CONTRACT",
  APP = "APP",
  GROUP = "GROUP",
  OPERATION = "OPERATION",
  ACCESS = "ACCESS",
  STATUS = "STATUS",
}

/** The filters of the multi-filter, in menu order, each with its group. ALL is the default instead. */
export const CLIENT_LIST_MULTI_FILTERS: Array<{ filter: ClientListFilter; group: ClientListFilterGroup }> = [
  { filter: ClientListFilter.IRES, group: ClientListFilterGroup.TYPE },
  { filter: ClientListFilter.CLIENT, group: ClientListFilterGroup.TYPE },
  { filter: ClientListFilter.CONTRACT_PURCHASE, group: ClientListFilterGroup.CONTRACT },
  { filter: ClientListFilter.CONTRACT_PERMANENT_RENT, group: ClientListFilterGroup.CONTRACT },
  { filter: ClientListFilter.CONTRACT_TESTING, group: ClientListFilterGroup.CONTRACT },
  { filter: ClientListFilter.SERVICE_CONTRACT, group: ClientListFilterGroup.CONTRACT },
  { filter: ClientListFilter.HAS_APP, group: ClientListFilterGroup.APP },
  // Its own group: inside Typ it would read "IRES or Master" (any within a group).
  { filter: ClientListFilter.MASTER, group: ClientListFilterGroup.GROUP },
  { filter: ClientListFilter.LIVE, group: ClientListFilterGroup.OPERATION },
  { filter: ClientListFilter.NOT_LIVE, group: ClientListFilterGroup.OPERATION },
  // Its own group, like Master: inside Status it would read "Aktiv or Gesperrt" (any within a group).
  { filter: ClientListFilter.BLOCKED, group: ClientListFilterGroup.ACCESS },
  { filter: ClientListFilter.ACTIVE, group: ClientListFilterGroup.STATUS },
  { filter: ClientListFilter.INACTIVE, group: ClientListFilterGroup.STATUS },
  { filter: ClientListFilter.DELETED, group: ClientListFilterGroup.STATUS },
];

/** Not deleted, active or inactive: exactly the old "Alle". */
export const DEFAULT_CLIENT_LIST_SELECTION: ClientListFilter[] = [ClientListFilter.ACTIVE, ClientListFilter.INACTIVE];

export function clientListFilterGroup(filter: ClientListFilter): ClientListFilterGroup | null {
  return CLIENT_LIST_MULTI_FILTERS.find((entry) => entry.filter === filter)?.group ?? null;
}

/**
 * One ticked filter on its own, WITHOUT the "not deleted" the single filters carry: in the
 * multi-filter, deletion is the Status group's business, so "IRES" + "Gelöscht" finds deleted IRES
 * clients. The rules themselves are the Android list's.
 */
function matchesOneFilter(client: ClientAdministrationRow, filter: ClientListFilter): boolean {
  switch (filter) {
    case ClientListFilter.ACTIVE:
      return !client.deleted && client.isActive !== false;
    case ClientListFilter.INACTIVE:
      return !client.deleted && client.isActive === false;
    case ClientListFilter.DELETED:
      return client.deleted;
    default:
      // Every other rule, evaluated as if the client were not deleted.
      return matchesClientListFilter({ ...client, deleted: false }, filter);
  }
}

/** Whether a client passes the ticked filters: any within a group, all groups with a tick. */
export function matchesClientListSelection(client: ClientAdministrationRow, selection: ClientListFilter[]): boolean {
  return matchesGroupedSelection(client, selection, clientListFilterGroup, matchesOneFilter);
}

/** Search, then the multi-filter, then sort by name; the input is not modified. */
export function selectClientListRowsBySelection(
  clients: ClientAdministrationRow[],
  selection: ClientListFilter[],
  query: string
): ClientAdministrationRow[] {
  return clients
    .filter((client) => matchesClientListSearch(client, query) && matchesClientListSelection(client, selection))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b), "de", { sensitivity: "base" }));
}

/** Whether a selection is the default one, so the header names no filter and the icon is not lit. */
export function isDefaultClientListSelection(selection: ClientListFilter[]): boolean {
  return (
    selection.length === DEFAULT_CLIENT_LIST_SELECTION.length &&
    DEFAULT_CLIENT_LIST_SELECTION.every((filter) => selection.includes(filter))
  );
}

/** The ticked filters in menu order, for the header ("Mandanten: 128 (Kunden, Servicevertrag)"). */
export function orderedClientListSelection(selection: ClientListFilter[]): ClientListFilter[] {
  return orderedSelection(selection, CLIENT_LIST_MULTI_FILTERS.map((entry) => entry.filter));
}

/**
 * The selection a single-filter link asks for (the IRES Admin cockpit's figures, `?filter=`): a
 * status filter on its own, any other filter plus the default status, so the list shows the same
 * clients the figure counted (the single filters exclude deleted clients).
 */
export function clientListSelectionForFilter(filter: ClientListFilter): ClientListFilter[] {
  if (filter === ClientListFilter.ALL) {
    return [...DEFAULT_CLIENT_LIST_SELECTION];
  }

  return clientListFilterGroup(filter) === ClientListFilterGroup.STATUS
    ? [filter]
    : [filter, ...DEFAULT_CLIENT_LIST_SELECTION];
}

/**
 * The selection a link asks for with `?filter=`, given once or repeated (the IRES Admin cockpit's
 * Gesamt card asks for Kunden and Echtbetrieb): each filter's own selection, joined. Null when the
 * link names no filter.
 */
export function clientListSelectionForLink(value: unknown): ClientListFilter[] | null {
  const values = (Array.isArray(value) ? value : [value]).filter((entry): entry is string => typeof entry === "string");

  if (values.length === 0) {
    return null;
  }

  return Array.from(new Set(values.flatMap((entry) => clientListSelectionForFilter(parseClientListFilter(entry)))));
}

/** Reads a stored selection: a JSON array of known filters; anything else is the default. */
export function parseClientListSelection(raw: string | null | undefined): ClientListFilter[] {
  if (!raw) {
    return [...DEFAULT_CLIENT_LIST_SELECTION];
  }

  try {
    const parsed = JSON.parse(raw);
    const known = CLIENT_LIST_MULTI_FILTERS.map((entry) => entry.filter);

    if (Array.isArray(parsed) && parsed.every((value) => known.includes(value))) {
      return parsed as ClientListFilter[];
    }
  } catch {
    // Not JSON (e.g. the literal text "undefined", or the single filter stored before the
    // multi-filter): fall through to the default rather than fail the screen.
  }

  return [...DEFAULT_CLIENT_LIST_SELECTION];
}
